'use strict'

/**
 * Returns video assets from imageGroups for a given color.
 * Priority: color-matched group → no-variation group → first video group.
 */
export const getVideoAssets = (imageGroups, colorValue) => {
    if (!Array.isArray(imageGroups) || !imageGroups.length) return []

    const videoGroups = imageGroups.filter((g) => g?.viewType === 'video')
    if (!videoGroups.length) return []

    if (colorValue) {
        const colorMatched = videoGroups.find((g) =>
            g.variationAttributes?.some(
                (attr) => attr?.id === 'color' && attr.values?.some((v) => v?.value === colorValue)
            )
        )
        if (colorMatched) return colorMatched.images || []
    }
    const ungrouped = videoGroups.find((g) => !g.variationAttributes?.length)
    if (ungrouped) return ungrouped.images || []

    return videoGroups[0]?.images || []
}

/**
 * Parses c_videoSequencePositions and returns the effective insertion slot.
 *
 * The priority rule means only one slot is ever active at a time:
 * - If position 1 is present in the list, returns [1] (video in primary slot).
 * - Otherwise if position 2 is present, returns [2] (video in hover slot).
 * - Otherwise returns [] (no video insertion).
 *
 * Examples:
 *   "1,3,5" → [1]   (position 1 takes priority)
 *   "2,3"   → [2]
 *   "3,5"   → []
 *   ""      → []
 */
export const parseSequencePositions = (raw) => {
    if (!raw) return []

    const positions = String(raw)
        .split(',')
        .map((s) => parseInt(s.trim(), 10))
        .filter((n) => !isNaN(n) && n >= 1)

    // PRIORITY RULE (IMPORTANT FIX)
    if (positions.includes(1)) return [1]
    if (positions.includes(2)) return [2]
    return []
}

/**
 * Builds a merged ordered sequence of image and video assets.
 *
 * Rules:
 * - positions are 1-based slots where the next video is inserted
 * - videos are inserted in their original order at each position
 * - remaining videos (if more videos than positions) are appended at the end
 * - no videos are inserted if positions array is empty
 *
 * Returns [{type: 'image'|'video', asset: {link, disBaseLink, alt, title}}]
 */
export const buildMediaSequence = (imageAssets, videoAssets, positions) => {
    const images = Array.isArray(imageAssets) ? imageAssets.filter(Boolean) : []
    const videos = Array.isArray(videoAssets) ? videoAssets.filter(Boolean) : []
    const insertPositions = Array.isArray(positions) ? positions : []

    if (!videos.length || !insertPositions.length) {
        return images.map((asset) => ({type: 'image', asset}))
    }

    const insertAt = new Set(insertPositions)
    const result = []
    let imgIdx = 0
    let vidIdx = 0
    let pos = 1
    const maxPos = images.length + videos.length + 2

    while ((imgIdx < images.length || vidIdx < videos.length) && pos <= maxPos) {
        if (insertAt.has(pos) && vidIdx < videos.length) {
            result.push({type: 'video', asset: videos[vidIdx++]})
        } else if (imgIdx < images.length) {
            result.push({type: 'image', asset: images[imgIdx++]})
        } else {
            result.push({type: 'video', asset: videos[vidIdx++]})
        }
        pos++
    }

    return result
}
